/**
 * Look settings of the Player's ☀ panel (issue walk-bake#11): `sensitivity`, an overall multiplier of how
 * far a drag turns the camera (touch and mouse), and `zoom`, how much that follows the focal length
 * (rotation per pixel ∝ (fov / 60°)^zoom: 0 = fixed as before, 1 = proportional to the field of view).
 * The defaults (1, 1) keep today's feel at the default 60° field of view. Saved per device; they reach
 * the viewer as `?look_sens=` / `?look_zoom=` when a walk opens and through
 * `walkbake.set({ lookSensitivity, lookZoom })` while it is open. The maths lives in the viewer
 * (public/viewer/app/sensitivity.js); the ranges here must match it.
 *
 * Pure functions apart from storage, so they run under node:test.
 */

import type { WalkbakeApi } from "./exposureSettings.ts";
import { clampRound, type NumericRange } from "./numeric.ts";

export interface LookSettings {
  /** Overall multiplier, 0.25..3, default 1 (= the feel before this setting existed, at 60°). */
  sensitivity: number;
  /** How much the sensitivity follows the zoom, 0..1, default 1. */
  zoom: number;
}

export const SENSITIVITY_RANGE: NumericRange = { min: 0.25, max: 3, step: 0.01 };
export const ZOOM_RANGE: NumericRange = { min: 0, max: 1, step: 0.01 };

export const DEFAULT_LOOK_SETTINGS: LookSettings = { sensitivity: 1, zoom: 1 };

const STORAGE_KEY = "walk-player.look";

function sanitize(raw: Partial<Record<keyof LookSettings, unknown>>, fallback: LookSettings): LookSettings {
  const pick = (value: unknown, range: NumericRange, base: number): number =>
    typeof value === "number" && Number.isFinite(value) ? clampRound(range, value) : base;
  return {
    sensitivity: pick(raw.sensitivity, SENSITIVITY_RANGE, fallback.sensitivity),
    zoom: pick(raw.zoom, ZOOM_RANGE, fallback.zoom),
  };
}

export function loadLookSettings(): LookSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return sanitize(JSON.parse(raw) as Partial<Record<keyof LookSettings, unknown>>, DEFAULT_LOOK_SETTINGS);
  } catch {
    // Blocked or corrupt storage: fall back to defaults.
  }
  return { ...DEFAULT_LOOK_SETTINGS };
}

export function saveLookSettings(settings: LookSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; still applies for this session.
  }
}

/** Overrides from the Player's own URL (`?look_sens=1.5&look_zoom=0.5`), for tests: win for that page, not saved. */
export function withLookUrlOverrides(settings: LookSettings, search: string): LookSettings {
  const params = new URLSearchParams(search);
  const number = (key: string): number | undefined => {
    const value = params.get(key);
    return value === null || value.trim() === "" ? undefined : Number(value);
  };
  return sanitize({ sensitivity: number("look_sens"), zoom: number("look_zoom") }, settings);
}

/** Query string for the viewer page: `look_sens` and `look_zoom`. */
export function lookQuery(settings: LookSettings): string {
  return new URLSearchParams({ look_sens: String(settings.sensitivity), look_zoom: String(settings.zoom) }).toString();
}

/** What to send an open viewer after `patch` changed `settings` (the new ones). */
export function lookViewerPatch(patch: Partial<LookSettings>, settings: LookSettings): Record<string, number> {
  return {
    ...(patch.sensitivity !== undefined && { lookSensitivity: settings.sensitivity }),
    ...(patch.zoom !== undefined && { lookZoom: settings.zoom }),
  };
}

/** Pushes changed look settings into an open viewer. */
export function applyLookToViewer(api: WalkbakeApi | null, patch: Record<string, number>): void {
  if (!api || Object.keys(patch).length === 0) return;
  api.set(patch);
}
